/**
 * React hook for field validation
 * Provides real-time validation for form fields
 */

import { useState, useCallback, useMemo } from 'react';
import { FieldSchema, ValidationResult } from '@/config/fieldConfig';
import { validateField, validateConfig, hasErrors, hasWarnings } from './validators';

export interface UseFieldValidationReturn {
  errors: Record<string, ValidationResult>;
  validateSingleField: (fieldName: string, value: any, fieldConfig: FieldSchema) => ValidationResult;
  validateAllFields: (config: Record<string, any>, fieldConfigs: Record<string, FieldSchema>) => void;
  clearError: (fieldName: string) => void;
  clearAllErrors: () => void;
  hasErrors: boolean;
  hasWarnings: boolean;
  getFieldError: (fieldName: string) => ValidationResult | undefined;
  isFieldValid: (fieldName: string) => boolean;
}

/**
 * Hook for managing field validation state
 */
export function useFieldValidation(appContext?: any): UseFieldValidationReturn {
  const [errors, setErrors] = useState<Record<string, ValidationResult>>({});

  /**
   * Validate a single field and update errors state
   */
  const validateSingleField = useCallback(
    (fieldName: string, value: any, fieldConfig: FieldSchema): ValidationResult => {
      const result = validateField(fieldName, value, fieldConfig, appContext);

      setErrors((prev) => {
        const updated = { ...prev };
        if (!result.valid) {
          updated[fieldName] = result;
        } else {
          delete updated[fieldName];
        }
        return updated;
      });

      return result;
    },
    [appContext]
  );

  /**
   * Validate all fields in the configuration
   */
  const validateAllFields = useCallback(
    (config: Record<string, any>, fieldConfigs: Record<string, FieldSchema>) => {
      const validationResults = validateConfig(config, fieldConfigs, appContext);
      setErrors(validationResults);
    },
    [appContext]
  );

  /**
   * Clear validation error for a specific field
   */
  const clearError = useCallback((fieldName: string) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  }, []);

  /**
   * Clear all validation errors
   */
  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  /**
   * Get validation error for a specific field
   */
  const getFieldError = useCallback(
    (fieldName: string): ValidationResult | undefined => {
      return errors[fieldName];
    },
    [errors]
  );

  /**
   * Check if a specific field is valid
   */
  const isFieldValid = useCallback(
    (fieldName: string): boolean => {
      const error = errors[fieldName];
      return !error || error.isWarning === true;
    },
    [errors]
  );

  /**
   * Check if there are any errors (excluding warnings)
   */
  const hasErrorsFlag = useMemo(() => hasErrors(errors), [errors]);

  /**
   * Check if there are any warnings
   */
  const hasWarningsFlag = useMemo(() => hasWarnings(errors), [errors]);

  return {
    errors,
    validateSingleField,
    validateAllFields,
    clearError,
    clearAllErrors,
    hasErrors: hasErrorsFlag,
    hasWarnings: hasWarningsFlag,
    getFieldError,
    isFieldValid,
  };
}

export default useFieldValidation;
